import { useEffect, type RefObject } from 'react'
import { DOMSerializer } from 'prosemirror-model'
import { devLog } from '@/lib/dev-log'
import { findLineRangesForExcerpt, pickNearestLineRange } from '@/lib/markdown-line-range'
import { type BlockLineMap, turndownService } from './tiptap-editor.utils'

interface UseTipTapCopyHandlerParams {
  editor: any
  documentId?: string
  documentTitle?: string
  documentPath?: string
  lineMapRef: RefObject<BlockLineMap>
  initialContent: string
  onCopyWithReference?: (data: { text: string; lineStart?: number; lineEnd?: number; isFullLine: boolean }) => void
}

export function useTipTapCopyHandler({
  editor,
  documentId,
  documentTitle,
  documentPath,
  lineMapRef,
  initialContent,
  onCopyWithReference,
}: UseTipTapCopyHandlerParams) {
  useEffect(() => {
    if (!editor || !documentId) return

    const copyHandler = (event: ClipboardEvent) => {
      const { state } = editor
      const { from, to } = state.selection

      if (from === to) return // No selection

      // Get selected text and HTML scoped to the selection only.
      const selectedText = state.doc.textBetween(from, to, '\n')
      const selectionSlice = state.selection.content()
      const selectionDiv = document.createElement('div')
      DOMSerializer.fromSchema(state.schema).serializeFragment(
        selectionSlice.content, { document: window.document }, selectionDiv
      )
      const selectedHTML = selectionDiv.innerHTML

      devLog('[TipTapEditor] Copy handler triggered (shadow line map):', {
        selectedTextLength: selectedText.length,
        lineMapSize: Object.keys(lineMapRef.current || {}).length
      })

      let lineStart: number | undefined
      let lineEnd: number | undefined
      let shouldCreateChip = false

      // Convert selected HTML to markdown
      const selectedMarkdown = turndownService.turndown(selectedHTML).trim()

      // Use shadow line map: read block IDs from selected TipTap nodes
      const blockIds: string[] = []

      state.doc.nodesBetween(from, to, (node: any) => {
        if (node.isBlock) {
          const blockId = node.attrs['data-block-id']
          if (blockId && lineMapRef.current && lineMapRef.current[blockId]) {
            blockIds.push(blockId)
            devLog('[TipTapEditor] Found block in selection:', {
              blockId,
              lines: `${lineMapRef.current[blockId].startLine + 1}-${lineMapRef.current[blockId].endLine + 1}`,
              nodeType: node.type.name
            })
          }
        }
      })

      // If we found block IDs, use them to get line numbers
      if (blockIds.length > 0 && lineMapRef.current) {
        const firstBlock = lineMapRef.current[blockIds[0]]
        const lastBlock = lineMapRef.current[blockIds[blockIds.length - 1]]

        if (firstBlock && lastBlock) {
          shouldCreateChip = true
          lineStart = firstBlock.startLine + 1 // Convert to 1-based
          lineEnd = lastBlock.endLine + 1

          devLog('[TipTapEditor] Chip created from block IDs:', {
            blockIds,
            lineStart,
            lineEnd,
            firstBlockLines: `${firstBlock.startLine + 1}-${firstBlock.endLine + 1}`,
            lastBlockLines: `${lastBlock.startLine + 1}-${lastBlock.endLine + 1}`
          })
        }
      } else {
        // Fallback: Block-level matching
        devLog('[TipTapEditor] No block IDs found, falling back to block-level matching')

        const sourceContent = initialContent || ''

        const normalizeBlock = (text: string) => {
          return text
            .trim()
            .replace(/\s+/g, ' ')
            .replace(/^#{1,6}\s*/, '')
            .replace(/^[-*+]\s+/, '')
            .replace(/^\d+\.\s+/, '')
            .replace(/^>\s*/, '')
            .replace(/```[\w]*\n?/g, '')
            .replace(/`([^`]+)`/g, '$1')
            .toLowerCase()
        }

        const splitIntoBlocks = (markdown: string): string[] => {
          const blocks: string[] = []
          const lines = markdown.split('\n')
          let currentBlock: string[] = []

          for (let i = 0; i < lines.length; i++) {
            const line = lines[i]
            const trimmed = line.trim()

            if (trimmed === '') {
              if (currentBlock.length > 0) {
                blocks.push(currentBlock.join('\n'))
                currentBlock = []
              }
              continue
            }

            const isBlockMarker = /^(#{1,6}|[-*+]|\d+\.|>|```)/.test(trimmed)

            if (isBlockMarker && currentBlock.length > 0) {
              blocks.push(currentBlock.join('\n'))
              currentBlock = [line]
            } else {
              currentBlock.push(line)
            }
          }

          if (currentBlock.length > 0) {
            blocks.push(currentBlock.join('\n'))
          }

          return blocks.filter(b => b.trim().length > 0)
        }

        const selectedBlocks = splitIntoBlocks(selectedMarkdown)
        const sourceBlocks = splitIntoBlocks(sourceContent)

        devLog('[TipTapEditor] Block analysis:', {
          selectedBlocksCount: selectedBlocks.length,
          sourceBlocksCount: sourceBlocks.length,
          firstSelectedBlock: selectedBlocks[0]?.substring(0, 80),
          lastSelectedBlock: selectedBlocks[selectedBlocks.length - 1]?.substring(0, 80)
        })

        if (selectedBlocks.length > 0) {
          const firstSelectedNormalized = normalizeBlock(selectedBlocks[0])
          const lastSelectedNormalized = normalizeBlock(selectedBlocks[selectedBlocks.length - 1])

          devLog('[TipTapEditor] Looking for blocks:', {
            firstSelectedNormalized: firstSelectedNormalized.substring(0, 60),
            lastSelectedNormalized: lastSelectedNormalized.substring(0, 60),
            lastSelectedBlockLength: lastSelectedNormalized.length
          })

          let startBlockIndex = -1
          let endBlockIndex = -1

          for (let i = 0; i < sourceBlocks.length; i++) {
            const normalizedSourceBlock = normalizeBlock(sourceBlocks[i])

            if (startBlockIndex === -1) {
              if (normalizedSourceBlock === firstSelectedNormalized) {
                startBlockIndex = i
                devLog('[TipTapEditor] Found start block (exact):', i)
              } else if (normalizedSourceBlock.includes(firstSelectedNormalized) ||
                         firstSelectedNormalized.includes(normalizedSourceBlock)) {
                const minLength = Math.min(normalizedSourceBlock.length, firstSelectedNormalized.length)
                const overlap = normalizedSourceBlock.includes(firstSelectedNormalized)
                  ? firstSelectedNormalized.length
                  : normalizedSourceBlock.length

                if (overlap >= minLength * 0.5) {
                  startBlockIndex = i
                  devLog('[TipTapEditor] Found start block (fuzzy):', i, {
                    overlap,
                    minLength,
                    ratio: (overlap / minLength).toFixed(2)
                  })
                }
              }
            }

            if (startBlockIndex >= 0 && i > startBlockIndex) {
              let isMatch = false
              let matchScore = 0

              if (normalizedSourceBlock === lastSelectedNormalized) {
                isMatch = true
                matchScore = 1.0
              } else if (normalizedSourceBlock.includes(lastSelectedNormalized) ||
                         lastSelectedNormalized.includes(normalizedSourceBlock)) {
                const minLength = Math.min(normalizedSourceBlock.length, lastSelectedNormalized.length)
                const overlap = normalizedSourceBlock.includes(lastSelectedNormalized)
                  ? lastSelectedNormalized.length
                  : normalizedSourceBlock.length

                matchScore = overlap / minLength

                if (matchScore >= 0.8) {
                  isMatch = true
                }
              }

              if (isMatch) {
                if (endBlockIndex === -1 || matchScore > 0.95 || (matchScore >= 0.8 && i > endBlockIndex)) {
                  endBlockIndex = i
                  devLog('[TipTapEditor] Found end block:', i, {
                    matchScore: matchScore.toFixed(2),
                    isExact: matchScore === 1.0
                  })
                }
              }
            }
          }

          devLog('[TipTapEditor] Block matching result:', {
            startBlockIndex,
            endBlockIndex,
            selectedBlocksCount: selectedBlocks.length
          })

          if (startBlockIndex >= 0 && endBlockIndex >= 0 && endBlockIndex >= startBlockIndex) {
            let blockStartLine = 0
            let blockEndLine = 0

            const sourceLinesForBlocks = sourceContent.split('\n')
            let currentBlockLines: number[] = []
            const blockToLines: number[][] = []

            for (let i = 0; i < sourceLinesForBlocks.length; i++) {
              const line = sourceLinesForBlocks[i]
              const trimmed = line.trim()

              if (trimmed === '') {
                if (currentBlockLines.length > 0) {
                  blockToLines.push([...currentBlockLines])
                  currentBlockLines = []
                }
                continue
              }

              const isBlockMarker = /^(#{1,6}|[-*+]|\d+\.|>|```)/.test(trimmed)

              if (isBlockMarker && currentBlockLines.length > 0) {
                blockToLines.push([...currentBlockLines])
                currentBlockLines = [i]
              } else {
                currentBlockLines.push(i)
              }
            }

            if (currentBlockLines.length > 0) {
              blockToLines.push([...currentBlockLines])
            }

            if (startBlockIndex < blockToLines.length) {
              const startBlockLines = blockToLines[startBlockIndex]
              blockStartLine = Math.min(...startBlockLines)
            }

            if (endBlockIndex < blockToLines.length) {
              const endBlockLines = blockToLines[endBlockIndex]
              blockEndLine = Math.max(...endBlockLines)
            }

            const blockRange = endBlockIndex - startBlockIndex + 1
            const selectedBlockCount = selectedBlocks.length

            const maxAllowedRange = selectedBlockCount === 1
              ? selectedBlockCount + 1
              : selectedBlockCount + 3

            devLog('[TipTapEditor] Range validation:', {
              blockRange,
              selectedBlockCount,
              maxAllowedRange,
              willPass: blockRange >= selectedBlockCount && blockRange <= maxAllowedRange
            })

            if (blockRange >= selectedBlockCount && blockRange <= maxAllowedRange) {
              shouldCreateChip = true
              lineStart = blockStartLine + 1
              lineEnd = blockEndLine + 1
              devLog('[TipTapEditor] Block match found!', {
                lineStart,
                lineEnd,
                blockRange,
                selectedBlockCount,
                blockStartLine,
                blockEndLine
              })
            } else {
              devLog('[TipTapEditor] Block range out of bounds:', {
                blockRange,
                selectedBlockCount
              })
            }
          } else {
            devLog('[TipTapEditor] Could not find matching blocks:', {
              startBlockIndex,
              endBlockIndex,
              foundStart: startBlockIndex >= 0,
              foundEnd: endBlockIndex >= 0
            })
          }
        }
      }

      // Copied text is the locator. Block ids are only a hint when the same
      // opening appears more than once (a parent list/blockquote id can be early).
      if (selectedMarkdown) {
        const textHits = findLineRangesForExcerpt(initialContent || '', selectedMarkdown)
        const picked = pickNearestLineRange(textHits, lineStart)
        if (picked?.lineStart) {
          lineStart = picked.lineStart
          lineEnd = picked.lineEnd ?? picked.lineStart
          shouldCreateChip = true
          devLog('[TipTapEditor] Chip lines from copied text:', {
            hits: textHits.length,
            lineStart,
            lineEnd,
          })
        }
      }

      devLog('[TipTapEditor] Copy handler result:', {
        shouldCreateChip,
        lineStart,
        lineEnd
      })

      if (shouldCreateChip && lineStart !== undefined && lineEnd !== undefined) {
        const clipboardData = {
          type: 'document-reference',
          documentId: documentId,
          filePath: documentPath || documentId,
          title: documentTitle || documentPath?.split('/').pop() || documentId.split('/').pop() || documentId,
          documentType: 'note',
          lineStart: lineStart,
          lineEnd: lineEnd,
          text: selectedMarkdown
        }

        if (event.clipboardData) {
          event.preventDefault()
          event.clipboardData.setData('text/plain', selectedText)
          event.clipboardData.setData('application/json', JSON.stringify(clipboardData))
          devLog('[TipTapEditor] Copy with reference (WYSIWYG):', {
            lineStart,
            lineEnd,
            documentId,
            selectedMarkdown: selectedMarkdown.substring(0, 50) + '...'
          })
        }

        onCopyWithReference?.({
          text: selectedMarkdown,
          lineStart: lineStart,
          lineEnd: lineEnd,
          isFullLine: true
        })
      } else {
        devLog('[TipTapEditor] Partial selection, no chip created')
        onCopyWithReference?.({
          text: selectedText,
          isFullLine: false
        })
      }
    }

    const editorDOM = editor.view.dom
    editorDOM.addEventListener('copy', copyHandler as EventListener)

    return () => {
      editorDOM.removeEventListener('copy', copyHandler as EventListener)
    }
  }, [editor, onCopyWithReference, documentId, documentTitle, documentPath, initialContent, lineMapRef])
}
